"use client"

import { useState, useEffect, useTransition } from "react"
import { useRouter, useParams } from "next/navigation"
import { Button, clx } from "@medusajs/ui"
import Heart from "@/modules/common/icons/heart"
import { addToWishlist, removeFromWishlist, getWishlistItem } from "@/lib/data/wishlist"
import { WishlistItem } from "@/types/wishlist"

type WishlistButtonProps = {
  variantId: string
  className?: string
  size?: "small" | "medium" | "large"
  showLabel?: boolean
  initialInWishlist?: boolean
  initialWishlistItemId?: string | null
}

/**
 * Button to add/remove a product variant from the wishlist.
 * Shows a heart icon that fills when the item is in the wishlist.
 */
export default function WishlistButton({
  variantId,
  className,
  size = "medium",
  showLabel = false,
  initialInWishlist = false,
  initialWishlistItemId = null,
}: WishlistButtonProps) {
  const router = useRouter()
  const params = useParams()
  const countryCode = params?.countryCode as string || "si"
  const [isPending, startTransition] = useTransition()
  const [inWishlist, setInWishlist] = useState(initialInWishlist)
  const [wishlistItemId, setWishlistItemId] = useState<string | null>(initialWishlistItemId)
  const [error, setError] = useState<string | null>(null)

  // Sync state when props change
  useEffect(() => {
    setInWishlist(initialInWishlist)
    setWishlistItemId(initialWishlistItemId)
  }, [initialInWishlist, initialWishlistItemId])

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setError(null)

    startTransition(async () => {
      try {
        if (inWishlist && wishlistItemId) {
          // Remove from wishlist
          await removeFromWishlist(wishlistItemId)
          setInWishlist(false)
          setWishlistItemId(null)
        } else {
          // Add to wishlist
          const wishlist = await addToWishlist(variantId)
          if (wishlist) {
            setInWishlist(true)
            // Find the new item ID
            const newItem = wishlist.items?.find(
              (item) => item.product_variant_id === variantId
            )
            if (newItem) {
              setWishlistItemId(newItem.id)
            }
          }
        }
      } catch (err: any) {
        // Check if error is Unauthorized and redirect to login
        const errorMessage = err?.message || ""
        const isUnauthorized = 
          err?.status === 401 || 
          err?.response?.status === 401 ||
          errorMessage.includes("Unauthorized") ||
          errorMessage.includes("must be logged in")
        
        if (isUnauthorized) {
          // Redirect to login page with country code
          router.push(`/${countryCode}/account`)
          return
        }
        
        setError(errorMessage || "Failed to update wishlist")
        console.error("Wishlist error:", err)
      }
    })
  }

  const iconSize = size === "small" ? "16" : size === "large" ? "24" : "20"
  const buttonSize = size === "small" ? "p-1.5" : size === "large" ? "p-3" : "p-2"

  return (
    <Button
      variant="secondary"
      className={clx(
        "rounded-full border-none shadow-none transition-colors duration-200",
        buttonSize,
        inWishlist 
          ? "bg-red-50 hover:bg-red-100 text-red-500" 
          : "bg-gray-100 hover:bg-gray-200 text-gray-600",
        className
      )}
      onClick={handleClick}
      disabled={isPending}
      title={inWishlist ? "Odstrani iz želja" : "Dodaj med želje"}
      aria-label={inWishlist ? "Odstrani iz želja" : "Dodaj med želje"}
    >
      <div className="flex items-center gap-2">
        {isPending ? (
          <div className="animate-spin">
            <Heart size={iconSize} />
          </div>
        ) : (
          <Heart size={iconSize} filled={inWishlist} />
        )}
        {showLabel && (
          <span className="text-sm">
            {inWishlist ? "V želji" : "Dodaj med želje"}
          </span>
        )}
      </div>
    </Button>
  )
}

/**
 * Wrapper component that fetches the initial wishlist state.
 * Use this when you need to check if the item is already in the wishlist.
 */
export function WishlistButtonWithState({
  variantId,
  ...props
}: Omit<WishlistButtonProps, "initialInWishlist" | "initialWishlistItemId">) {
  const [wishlistItem, setWishlistItem] = useState<WishlistItem | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const checkWishlist = async () => {
      try {
        const item = await getWishlistItem(variantId)
        setWishlistItem(item)
      } catch (error) {
        console.error("Error checking wishlist:", error)
      } finally {
        setLoading(false)
      }
    }

    checkWishlist()
  }, [variantId])

  if (loading) {
    return (
      <Button
        variant="secondary"
        className="rounded-full border-none shadow-none bg-gray-100 p-2"
        disabled
      >
        <Heart size="20" />
      </Button>
    )
  }

  return (
    <WishlistButton
      variantId={variantId}
      initialInWishlist={!!wishlistItem}
      initialWishlistItemId={wishlistItem?.id || null}
      {...props}
    />
  )
}















